"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import {
  agentDeliverySettingsQueryKey,
  tenantDeliveryDefaultsQueryKey,
} from "@/hooks/queries/delivery-bindings"
import { getQueryTenantId } from "@/lib/auth/query-tenant-id"
import {
  agentIntegrationStatusesQueryKey,
  agentIntegrationStatusesTenantQueryKey,
} from "@/hooks/queries/agent-integration-status-keys"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import {
  clearTenantDeliveryDefaultsAction,
  saveAgentCrmStatusFallbackAction,
  type SaveCrmStatusFallbackInput,
  saveAgentDeliverySettingsAction,
  saveTenantDeliveryDefaultsAction,
  type AgentDeliverySettingsView,
  type SaveAgentDeliverySettingsInput,
  type SaveTenantDeliveryDefaultsInput,
  type TenantDeliveryDefaultsView,
} from "@/server-actions/delivery-bindings"
import { useTranslations } from "next-intl"


type FormErrorSetter = (errors: Record<string, unknown>) => void

export function useSaveCrmStatusFallbackMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryBindings")

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getQueryTenantId(user)
  return useMutation({
    mutationFn: async (input: SaveCrmStatusFallbackInput & { setErrors?: FormErrorSetter }) => {
      const { setErrors, ...values } = input
      const result = await withQueryHandling(saveAgentCrmStatusFallbackAction(values), {
        loading: tUi("savingCRMStatusFallback"), success: tUi("cRMStatusFallbackSaved"), setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: value => {
      queryClient.setQueryData(agentDeliverySettingsQueryKey(tenantId, value.scopeType, value.scopeId), value)
    },
  })
}

export function useSaveTenantDeliveryDefaultsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryBindings")

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getQueryTenantId(user)
  const queryKey = tenantDeliveryDefaultsQueryKey(tenantId)

  return useMutation({
    mutationFn: async (input: SaveTenantDeliveryDefaultsInput & {
      setErrors?: FormErrorSetter
    }): Promise<TenantDeliveryDefaultsView> => {
      const { setErrors, ...actionInput } = input
      const result = await withQueryHandling(
        saveTenantDeliveryDefaultsAction(actionInput),
        {
          loading: tUi("savingDeliveryDefaults"),
          success: tUi("deliveryDefaultsSavedForAllAgents"),
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async value => {
      queryClient.setQueryData(queryKey, value)
      await queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesTenantQueryKey(tenantId),
      })
    },
  })
}

export function useClearTenantDeliveryDefaultsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryBindings")

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getQueryTenantId(user)
  const queryKey = tenantDeliveryDefaultsQueryKey(tenantId)

  return useMutation({
    mutationFn: async (): Promise<TenantDeliveryDefaultsView> => {
      const result = await withQueryHandling(
        clearTenantDeliveryDefaultsAction({}),
        {
          loading: tUi("clearingDeliveryDefaults"),
          success: tUi("deliveryDefaultsCleared"),
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async value => {
      queryClient.setQueryData(queryKey, value)
      await queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesTenantQueryKey(tenantId),
      })
    },
  })
}

export function useSaveAgentDeliverySettingsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsDeliveryBindings")

  const queryClient = useQueryClient()
  const { user } = useAuth()
  const tenantId = getQueryTenantId(user)

  return useMutation({
    mutationFn: async (input: SaveAgentDeliverySettingsInput & {
      setErrors?: FormErrorSetter
    }): Promise<AgentDeliverySettingsView> => {
      const { setErrors, ...actionInput } = input
      const result = await withQueryHandling(
        saveAgentDeliverySettingsAction(actionInput),
        {
          loading: tUi("savingDeliverySettings"),
          success: tUi("deliverySettingsSaved"),
          setErrors,
        },
      )
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async value => {
      queryClient.setQueryData(
        agentDeliverySettingsQueryKey(
          tenantId,
          value.scopeType,
          value.scopeId,
        ),
        value,
      )
      await queryClient.invalidateQueries({
        queryKey: agentIntegrationStatusesQueryKey(
          tenantId,
          value.scopeType,
        ),
      })
    },
  })
}
